// veevalidate settings
import Vue from 'vue'
import store from '@/store'
import {
  ValidationProvider,
  ValidationObserver,
  localize,
  extend
} from 'vee-validate/dist/vee-validate.full'
import { min, between, length } from 'vee-validate/dist/rules'
import tw from 'vee-validate/dist/locale/zh_TW.json'
import { interpolate, verifyForeignId } from '@/utils'
import {
  isNationalIdentificationNumberValid,
  isResidentCertificateNumberValid,
  isGuiNumberValid
} from 'taiwan-id-validator'

tw.messages = {
  ...tw.messages,
  required: '請輸入 {_field_}'
}

localize({ [tw.code]: tw })
localize(tw.code)

// custom messages
const PHONE = /.*電話.*|.*碼.*/

extend('min', {
  ...min,
  message (field, values) {
    const { _field_, length } = values
    if (PHONE.test(field)) {
      return `${_field_} 至少輸入 ${length} 碼`
    }
    return interpolate(tw.messages.min, values)
  }
})

extend('length', {
  ...length,
  message (field, values) {
    if (values && values.msg) {
      return values.msg
    }
    return interpolate(tw.messages.length, values)
  }
})

extend('num_length', {
  message (field, values) {
    return `${field} 的長度須為${values.len}且為數字`
  },
  validate: (value, { len }) => {
    if (value && (isNaN(value) || value.length !== Number(len))) {
      return false
    }
    return true
  },
  params: ['len']
})

extend('paswbetween', {
  message (field, values) {
    const { min, max } = values
    return `密碼長度至少${min}碼，至多輸入${max}碼！`
  },
  validate: (value, { min, max }) => {
    const len = value === undefined || value === null ? 0 : String(value).length
    return len >= Number(min) && len <= Number(max)
  },
  params: ['min', 'max']
})

extend('continuous', { // 不可為連續3個相同英文字(AAA)、連續3個相同數字(222、000)、連續3個連號數字(123、345、890)、連續3個連續英文數字(Abc、efg)
  computesRequired: true,
  validate: value => {
    const { length } = value
    const regExp = /^[\d|a-zA-Z]+$/
    let continuous = true
    let valueList = [...value] // String to 'char array'
    for( let i=2;i<length;i++) {
      value = value.toLowerCase()
      let charTop = value.charCodeAt(i)
      let charSec = value.charCodeAt(i-1)
      let charThrid = value.charCodeAt(i-2)
      if ((charTop === (charSec+1) && (charSec+1) === (charThrid+2) && regExp.test(value[i])) ||
         (charTop === charSec && charSec === charThrid && regExp.test(value[i])) ||
         value.includes('890') ) {
        continuous = false
      }
    }
    return continuous
  },
  message: field => '密碼字串中不得設定為: 連續3個相同英文字(AAA)、連續3個相同數字(222、000)、連續3個連號數字(123、345、890)、連續3個連續英文數字(Abc、efg)'
  })

  extend('includeNumAndWord', { // 字串包含數字跟英文
    computesRequired: true,
    validate: value => {
      const { length } = value
      const regWord = /^[a-zA-Z]+$/  // 純英文檢核
      const regNum = /^\d+$/  // 純數字檢核
      let includeNum = false
      let includeWord = false
      for( let i=0;i<length;i++) {
        if (regWord.test(value[i])) {
          includeNum = true
        }
        if (regNum.test(value[i])) {
          includeWord = true
        }
      }
        return includeNum && includeWord
      },
    message: field => '至少輸入1個英文字和1個數字，且只能輸入英數字和符號！'
    })

extend('required_prefix0', {
  computesRequired: true,
  validate: value => {
    if (!value || value.length <= 0) {
      return false
    }
    return true
  },
  message: field => `${field}`
})

extend('required_y', {
  computesRequired: true,
  validate: value => {
    if (value !== 'Y') {
      return false
    }
    return true
  },
  message: field => `${field}`
})

// custom rules
// required_if_yn
extend('required_if_yn', {
  computesRequired: true,
  message: (field, values) => {
    if (values && values.msg) {
      return values.msg
    }
    return interpolate(tw.messages.required_if, values)
  },
  validate: (value, { yn, msg }) => {
    const required = yn === 'Y'
    let valid = true
    if (required) {
      valid = !!value
    }

    return { valid, required }
  },
  params: [{ name: 'yn', isTarget: true }, 'msg']
})

// 被保險人和要保人是否同一id
extend('isSameInsuredId', {
  computesRequired: true,
  message: field => '身分證/居留證號碼與要保人相同，請確認!',
  validate: (value, { cusType, idn, insureIdentical }) => {
    cusType = cusType.trim()
    if (cusType === 'I' && insureIdentical === 'N') {
      if (value === idn) {
        return false
      }
    }
    return true
  },
  params: ['cusType', 'idn', 'insureIdentical']
})

// 身分證不可與需法定代理人同一人
extend('isSameLegal', {
  computesRequired: true,
  message: field => '身分證不可與需法定代理人同一人，請確認!',
  validate: (value, { idn }) => {
    if (value === idn) {
      return false
    }
    return true
  },
  params: ['idn']
})

// 身分證驗證和居留證驗證
extend('mixId', {
  validate: value => {
    if (isNationalIdentificationNumberValid(value)) {
      return true
    } else {
      if (value && value.length >= 2) {
        if ('ABCD89'.indexOf(value[1]) === -1 || value[0] === 'Y') {
          return false
        }
      }
      return isResidentCertificateNumberValid(value) || verifyForeignId(value)
    }
  },
  message: field => `${field} 有誤，請確認!`
})

// 居留證驗證
extend('residentId', {
  validate: value => {
    if (value && value.length >= 2) {
      if ('ABCD89'.indexOf(value[1]) === -1 || value[0] === 'Y') {
        return false
      }
    }
    return isResidentCertificateNumberValid(value) || verifyForeignId(value)
  },
  message: field => `${field} 有誤，請確認!`
})

// 身分證驗證
extend('twId', {
  validate: value => {
    return isNationalIdentificationNumberValid(value)
  },
  message: field => `${field} 有誤，請確認!`
})
// gender check by twId
/*
const genderType = {
  M: '1',
  F: '2'
}
*/
extend('gender', {
  computesRequired: true,
  message: field => '性別與身分證/居留證號碼不符，請確認!',
  validate: (value, { nationality, id, polKind }) => {
    if ((value === 'M' || value === '1') && polKind === 'M') {
      return '投保婦嬰險應為女性，請確認！'
    }
    if (value && id && nationality === 'TW') {
      let second = id.trim().substring(1, 2)
      return value === second
    } else if (value && id && nationality !== 'TW') {
      let second = id.trim().substring(1, 2)
      if (second === 'A' || second === 'C' || second === '8') {
        return value === '1'
      } else if (second === 'B' || second === 'D' || second === '9') {
        return value === '2'
      }
      return true
    }
    return true
  },
  params: [
    { name: 'nationality', isTarget: true },
    { name: 'id', isTarget: true },
    'polKind'
  ]
})
// 檢核連絡電話
extend('connectionPhone', {
  computesRequired: true,
  message: field => '電話格式錯誤，請確認!',
  validate: value => {
    const reg = /^([0-9#-])*$/ // 判斷連絡電話 空白 ',' '-'
    if (reg.test(value)) {
      return true
    }
  }
})
// 檢核行動電話
extend('mobilePhone', {
  computesRequired: true,
  message: field => '行動電話格式錯誤，請確認!',
  validate: value => {
    const reg = /^09[0-9]{8}$/ // 判斷09開頭 後面8個數字
    if (reg.test(value)) {
      return true
    }
  }
})
extend('mobilePhone2', { // 為空時不判
  computesRequired: false,
  message: field => '行動電話格式錯誤，請確認!',
  validate: value => {
    const reg = /^09[0-9]{8}$/ // 判斷09開頭 後面8個數字
    if (reg.test(value)) {
      return true
    }
  }
})

// 檢核帳號碼數
extend('accNoRule', {
  computesRequired: true,
  message: field => '帳號碼數有誤，請確認!',
  validate: (value, { len }) => {
    let first = len.trim().substring(0, 2)
    let second = len.trim().substring(2)
    if (first !== '00') {
      if (second !== '00') {
        if (value.length === Number(first) || value.length === Number(second)) {
          return true
        } else {
          return false
        }
      } else {
        if (value.length !== Number(first)) {
          return false
        }
      }
    } else {
      if (second !== '00') {
        if (value.length !== Number(second)) {
          return false
        }
      }
    }
    return true
  },
  params: ['len']
})

// address
const ADDRESS_NOT_ACCEPTED = /.*郵政.*/
const ADDRESS2_NOT_ACCEPTED = /.*郵局.*/
const ADDRESS_REQUIRED = /.*號.*/
extend('address', {
  validate: value => {
    if (ADDRESS_NOT_ACCEPTED.test(value) || ADDRESS2_NOT_ACCEPTED.test(value)) {
      return '請輸入地址(不受理郵政信箱)'
    }
    if (!ADDRESS_REQUIRED.test(value)) {
      return '地址需包括號，請確認!'
    }
    return true
  }
})
// phoneGroup
extend('phoneGroup', {
  computesRequired: true,
  message: field => '公司/住宅/手機電話，請擇一填寫',
  validate: (value, { target1, target2, target3 }) => {
    const valid = !!(target1 || target2 || target3)
    const required = !(target1 || target2 || target3)
    return { valid, required }
  },
  params: [
    { name: 'target1', isTarget: true },
    { name: 'target2', isTarget: true },
    { name: 'target3', isTarget: true }
  ]
})

extend('insuredMobileCheck', {
  message: field => '要保人與被保險人手機號碼不可相同，請確認!',
  validate: (value, { phone }) => {
    if (value === phone) {
      return false
    }
    return true
  },
  params: [
    { name: 'phone' }
  ]
})
// chaptcha validate
extend('chaptcha', {
  message: field => '驗證碼不正確，請重新輸入',
  validate: value => {
    if (process.env.NODE_ENV === 'development' && value === '999999') {
      store.dispatch('user/validateCaptcha', value) // 消session attribute
      return true
    }
    return store.dispatch('user/validateCaptcha', value).then(r => {
      const { data, status } = r

      if (status === 200 && typeof data === 'boolean') {
        return !!r.data
      }
      if (status === 500) {
        return '驗證碼已過期，請重新輸入'
      }

      return false
    })
  }
})
extend('purRule', {
  computesRequired: true,
  message: field => `不得僅填寫【理財】、【節稅】`,
  validate: value => {
    let purCnt = 0
    let otherCnt = 0
    value.forEach(e => {
      if (e === '9') {
        purCnt = purCnt + 1
      } else {
        otherCnt = otherCnt + 1
      }
    })
    console.log('====>otherCnt:' + otherCnt + ',purCnt:' + purCnt)
    if (otherCnt === 0 && purCnt === 1) {
      return false
    } else {
      return true
    }
  }
})

extend('isGratherThanZeor', {
  computesRequired: true,
  message: field => '只能輸入大於0的數值，請確認!',
  validate: value => {
    let val = parseFloat(value)
    if (val < 1) {
      return false
    } else {
      return true
    }
  }
})


// 檢核是否為純數字
extend('valideNum', {
  computesRequired: true,
  message: field => '只能輸入數字，請確認!',
  validate: value => {
    const reg = /^\d+$/
    if (reg.test(value)) {
      return true
    }
  }
})

// 檢核是否為正負數字
extend('checkNumWithDecimal', {
  computesRequired: true,
  message: field => '只能輸入正負數字，請確認!',
  validate: value => {
    const reg = /^-?(0|[1-9]\d*)$/
    if (reg.test(value)) {
      return true
    }
  }
})

// 小數
extend('isDecimalPoint', {
  message: field => '',
  validate: (value, { intNum, pointNum }) => {
    intNum = intNum.trim() || '1'
    pointNum = pointNum.trim() || '0'

    if (pointNum === '0') {
      // 整數
      let reg = new RegExp('^[0-9]{1,' + intNum + '}$')
      if (reg.test(value)) {
        return true
      } else {
        return '僅可輸入' + intNum + '位整數'
      }
    } else {
      // 有小數
      let reg1 = new RegExp('^[0-9]{1,' + intNum + '}$')
      let reg2 = new RegExp('^[0-9]{1,' + intNum + '}(\\.[0-9]{1,' + pointNum + '})$')
      if (reg1.test(value) || reg2.test(value)) {
        return true
      } else {
        return '僅可輸入' + intNum + '位整數及小數點後' + pointNum + '位'
      }
    }
  },
  params: [
    { name: 'intNum' },
    { name: 'pointNum' }
  ]
})

// 小數
extend('isDecimalPoint2', {
  message: field => '',
  validate: (value, { intNum, pointNum }) => {
    intNum = intNum.trim() || '1'
    pointNum = pointNum.trim() || '0'
    if (pointNum === '0') {
      // 整數
      let reg = new RegExp('^[0-9]{1,' + intNum + '}$')
      if (reg.test(value)) {
        return true
      } else {
        return '僅可輸入' + intNum + '位整數'
      }
    } else {
      // 有小數
      let reg1 = new RegExp('^[0-9]{1,' + intNum + '}$')
      let reg2 = new RegExp('^[0-9]{1,' + intNum + '}(\\.[0-9]{1,' + pointNum + '})$')
      if (reg1.test(value) || reg2.test(value)) {
        let val = parseFloat(value)
        if (val > 100) {
          return '輸入區間為100~0.01，100後不可輸入小數'
        } else if (val === 100) {
          if (!reg1.test(value)) {
            return '輸入區間為100~0.01，100後不可輸入小數'
          }
        }
        return true
      } else {
        return '輸入區間為100~0.01，100後不可輸入小數'
      }
    }
  },
  params: [
    { name: 'intNum' },
    { name: 'pointNum' }
  ]
})
// 檢核email是否有包含mega
extend('megaEmail', {
  computesRequired: true,
  message: field => '不可填寫公司E-mail!',
  validate: value => {
    const reg = /@mega/
    if (!reg.test(value)) {
      return true
    }
    return false
  }
})
// 日期 YYYYMMDD
extend('isDateYYYYMMDD', {
  validate: value => {
    const regEx = /^\d{4}\d{2}\d{2}$/
    if (!value.match(regEx)) return '格式錯誤，格式為 YYYYMMDD'
    const year = value.substring(0, 4)
    const month = value.substring(4, 6)
    const day = value.substring(6, 8)
    const v2 = `${year}-${month}-${day}`
    const d = new Date(v2)
    const dNum = d.getTime()
    if (!dNum && dNum !== 0) return '日期錯誤'
    if (d.toISOString().slice(0, 10) !== v2) return '日期錯誤'
    return true
  }
})

// 「就診期間-迄日」不可小於「就診期間-起日
extend('de_required_geTo', {
  validate: (value, { endDate, startDate }) => {
    if (endDate && startDate) {
      if (endDate < startDate) {
        return false
      }
    }
    return true
  },
  params: [
    { name: 'endDate' },
    { name: 'startDate' }
  ],
  message: field => '「就診期間-迄日」不可小於「就診期間-起日」'
})

// 檢核是否仟元為單位
extend('isThousandUnit', {
  computesRequired: true,
  message: field => '保費需以仟元為單位，請調整!',
  validate: value => {
    if (value) {
      if ((parseInt(value) % 1000) === 0) {
        return true
      }
    }
  }
})

extend('uploadRequired', {
  computesRequired: true,
  message: field => '請選擇上傳檔案!',
  validate: value => {
    if (value) {
      return true
    }
  }
})

// combox檢核帳號碼數
// 檢核帳號碼數
// combox檢核帳號碼數
extend('accNoRule2', {
  computesRequired: true,
  message (field, { len }) {
    const first = len.trim().substring(0, 2)
    const second = len.trim().substring(2)
    if (second !== '00' && first !== '00') {
      return `${field} 碼數有誤，請輸入${second}或${first}長度數字 ，請確認!`
    } else if (second === '00' && first !== '00') {
      return `${field} 碼數有誤，請輸入${first}長度數字，請確認!`
    } else {
      return `${field} 碼數有誤，請確認!`
    }
  },
  validate: (value, { len }) => {
    let first = len.trim().substring(0, 2)
    let second = len.trim().substring(2)
    if (first !== '00') {
      if (second !== '00') {
        if (value.acctNo) {
          if (value.acctNo.length === Number(first) || value.acctNo.length === Number(second)) {
            return true
          } else {
            return false
          }
        } else {
          if (value.length === Number(first) || value.length === Number(second)) {
            return true
          } else {
            return false
          }
        }
      } else {
        if (value.acctNo) {
          if (value.acctNo.length !== Number(first)) {
            return false
          }
        } else {
          if (value.length !== Number(first)) {
            return false
          }
        }
      }
    } else {
      if (second !== '00') {
        if (value.acctNo) {
          if (value.acctNo.length !== Number(second)) {
            return false
          }
        } else {
          if (value.length !== Number(second)) {
            return false
          }
        }
      }
    }
    return true
  },
  params: ['len']
})

extend('showMsg', {
  computesRequired: true,
  validate: (value, { msg }) => {
    if (msg) {
      return msg
    }
    return false
  },
  params: ['msg']
})
// add by SCR #93149 , 檢核 家庭年收入 與 工作年收入 、 其他收入的判斷
extend('checkHIncomeMinFunc', {
  params: ['relNo', 'hIncome', 'salary', 'oIncome', 'o_hIncome', 'o_salary', 'o_oIncome' , 'uiName'],
  message: field => '',
  validate: (value , { relNo, hIncome, salary, oIncome, o_hIncome, o_salary, o_oIncome ,uiName }) => {
    let message
    let chk = false
    if(!relNo) return true
    if(relNo == '1' || relNo == '2'){ //「與要保人關係」選擇 '本人'和'配偶'時
      message = ''
      hIncome = parseFloat(hIncome) || 0
      salary = parseFloat(salary) || 0
      oIncome = parseFloat(oIncome) || 0
      o_hIncome = parseFloat(o_hIncome) || 0
      o_salary = parseFloat(o_salary) || 0
      o_oIncome = parseFloat(o_oIncome) || 0
      if(relNo == '2'){
        if(uiName == 'hIncome' && hIncome != o_hIncome){
          chk = false
          message = '要保人與被保險人關係為配偶，家庭年收入需一致'
        }
      }
      if(!message){//訊息為空才繼續判斷
        if(hIncome < (salary+oIncome+o_salary+o_oIncome)){
          message = '要保人與被保險人關係為配偶，家庭年收入不可低於要保人與被保險人工作年收入+其他收入合計金額!'
          chk = false
        }else{
          chk = true
        }
      }
    }else{
      message = '家庭年收入需大於或等於個人年收入+其他年收入，不得小於!'
      hIncome = parseFloat(hIncome) || 0
      salary = parseFloat(salary) || 0
      oIncome = parseFloat(oIncome) || 0
      if(hIncome < (salary+oIncome)){
        chk = false
      }else{
        chk = true
      }
    }
    if (!chk) {
      return message
    }
    return chk
  }
})

Vue.component('validation-provider', ValidationProvider)
Vue.component('validation-observer', ValidationObserver)
